import React, { useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { StarIcon, CheckIcon, AlertCircleIcon, ArrowRightIcon, UndoIcon, TagIcon, PackageIcon, DollarSignIcon, ClockIcon, ShoppingBagIcon, BoxIcon, LinkIcon } from 'lucide-react';
import Header from '../components/Header';
import { Link } from 'react-router-dom';
// Sample product data
const sampleProduct = {
  id: '1',
  title: 'Black Wireless Headphones Pro',
  sku: 'ELEC-0001',
  price: 149.99,
  inventory: 45,
  category: 'Tech',
  imageUrl: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
  matchCategory: 'perfect-match',
  amazonProduct: {
    id: 'amz1',
    title: 'Black Wireless Headphones Pro - Amazon Edition',
    asin: 'B01ABCDEF',
    price: 159.99,
    category: 'Tech',
    imageUrl: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
    rating: 4.5,
    reviewCount: 1234
  }
};
// Base Card Component with different animation variants
const AnimationCard = ({
  title,
  animationVariant
}) => {
  const [isLinked, setIsLinked] = useState(false);
  const [isSlimmed, setIsSlimmed] = useState(false);
  const [showArrowTransform, setShowArrowTransform] = useState(false);
  const handleLink = () => {
    if (animationVariant === 'arrowTransform' || animationVariant === 'arrowSwipe') {
      // First slim down the row
      setIsSlimmed(true);
      // Then after the row is slimmed, show the arrow transform
      setTimeout(() => {
        setShowArrowTransform(true);
      }, 600); // Wait for the slimming animation to complete
    } else {
      // For other animation variants, just set linked immediately
      setIsLinked(true);
    }
  };
  const handleUndo = () => {
    setIsLinked(false);
    setIsSlimmed(false);
    setShowArrowTransform(false);
  };
  // Component for comparison rows
  const ComparisonRow = ({
    label,
    shopify,
    amazon,
    icon
  }) => <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-344">
      <div className="flex items-center space-x-2" data-id="element-345">
        <div className="w-6" data-id="element-346">{icon}</div>
        <span className="text-gray-600" data-id="element-347">{shopify}</span>
      </div>
      <div className="flex items-center space-x-2" data-id="element-348">
        <div className="w-6" data-id="element-349">{icon}</div>
        <span className="text-gray-800" data-id="element-350">{amazon}</span>
      </div>
    </div>;
  // Different animation variants
  const animationVariants = {
    // 1. Current fade & slide (default)
    fadeSlide: {
      initial: {
        opacity: 0,
        y: -20
      },
      animate: {
        opacity: 1,
        y: 0
      },
      exit: {
        opacity: 0,
        y: 20
      },
      transition: {
        duration: 0.4
      }
    },
    // 2. Scale & fade
    scaleFade: {
      initial: {
        opacity: 0,
        scale: 0.8
      },
      animate: {
        opacity: 1,
        scale: 1
      },
      exit: {
        opacity: 0,
        scale: 1.1
      },
      transition: {
        duration: 0.4
      }
    },
    // 3. Flip animation
    flip: {
      initial: {
        opacity: 0,
        rotateX: 90
      },
      animate: {
        opacity: 1,
        rotateX: 0
      },
      exit: {
        opacity: 0,
        rotateX: -90
      },
      transition: {
        duration: 0.6
      }
    },
    // 4. Bounce effect
    bounce: {
      initial: {
        opacity: 0,
        y: -50
      },
      animate: {
        opacity: 1,
        y: 0
      },
      exit: {
        opacity: 0,
        y: 50
      },
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 15
      }
    },
    // 5. Swipe left/right
    swipe: {
      initial: {
        opacity: 0,
        x: -300
      },
      animate: {
        opacity: 1,
        x: 0
      },
      exit: {
        opacity: 0,
        x: 300
      },
      transition: {
        duration: 0.5
      }
    },
    // 6. Arrow Transform
    arrowTransform: {
      initial: {
        opacity: 0
      },
      animate: {
        opacity: 1
      },
      exit: {
        opacity: 0
      },
      transition: {
        duration: 0.4
      }
    },
    // 7. Arrow Transform + Swipe success
    arrowSwipe: {
      initial: {
        opacity: 0
      },
      animate: {
        opacity: 1
      },
      exit: {
        opacity: 0
      },
      transition: {
        duration: 0.4
      }
    }
  };
  const selectedVariant = animationVariants[animationVariant];
  const product = sampleProduct;
  // Special case for Arrow to Swipe animation variant
  if (animationVariant === 'arrowSwipe') {
    return <div className="mb-8" data-id="element-351">
        <h2 className="text-lg font-semibold text-gray-800 mb-3" data-id="element-352">{title}</h2>
        <motion.div layout className="bg-white rounded-lg shadow-sm overflow-hidden border relative border-gray-200" data-id="element-353">
          <div className="p-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between" data-id="element-354">
            <div className="flex items-center space-x-2" data-id="element-355">
              <CheckIcon className="h-4 w-4 text-green-500" data-id="element-356" />
              <h3 className="text-sm font-medium text-gray-700" data-id="element-357">
                Perfect Match
              </h3>
            </div>
          </div>
          <AnimatePresence mode="wait" data-id="element-358">
            {isLinked ? <motion.div layout initial={{
            opacity: 0,
            x: -300
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: 300
          }} transition={{
            duration: 0.5
          }} className="p-3 flex items-center justify-between bg-green-50" data-id="element-359">
                <div className="flex items-center min-w-0" data-id="element-360">
                  <CheckIcon className="h-4 w-4 flex-shrink-0 mr-2 text-green-500" data-id="element-361" />
                  <div className="min-w-0" data-id="element-362">
                    <span className="text-sm font-medium truncate block text-green-700" data-id="element-363">
                      {product.title}
                    </span>
                    <span className="text-xs text-green-600" data-id="element-364">
                      Product Added for Linking
                    </span>
                  </div>
                </div>
                <motion.button onClick={handleUndo} className="ml-4 flex items-center text-sm text-green-600 hover:text-green-700" whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-365">
                  <UndoIcon className="h-4 w-4 mr-1" data-id="element-366" />
                  Undo
                </motion.button>
              </motion.div> : isSlimmed ? <motion.div layout initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.4
          }} className="p-3 flex items-center justify-between" data-id="element-367">
                <motion.div layout className="flex items-stretch w-full" data-id="element-368">
                  <motion.div layout className="flex-1 pr-4" data-id="element-369">
                    <motion.div className="text-sm font-medium text-gray-700 truncate" data-id="element-370">
                      {product.title}
                    </motion.div>
                  </motion.div>
                  <motion.div layout className="flex items-center justify-center w-12" style={{
                perspective: 800
              }} data-id="element-371">
                    <AnimatePresence mode="wait" data-id="element-372">
                      {!showArrowTransform ? <motion.div key="arrow" layout className="bg-blue-50 rounded-full p-2" exit={{
                    rotateY: 90,
                    opacity: 0
                  }} transition={{
                    duration: 0.3,
                    type: 'spring',
                    stiffness: 100
                  }} data-id="element-373">
                          <ArrowRightIcon className="h-4 w-4 text-blue-500" data-id="element-374" />
                        </motion.div> : <motion.div key="link" layout className="bg-blue-100 p-2 rounded-full" initial={{
                    rotateY: -90,
                    opacity: 0
                  }} animate={{
                    rotateY: 0,
                    opacity: 1
                  }} transition={{
                    duration: 0.3,
                    type: 'spring',
                    stiffness: 100
                  }} onAnimationComplete={() => {
                    setTimeout(() => {
                      setIsLinked(true);
                    }, 300);
                  }} data-id="element-375">
                          <LinkIcon className="h-4 w-4 text-blue-600" data-id="element-376" />
                        </motion.div>}
                    </AnimatePresence>
                  </motion.div>
                  <motion.div layout className="flex-1 pl-4" data-id="element-377">
                    <motion.div className="text-sm font-medium text-gray-800 truncate" data-id="element-378">
                      {product.amazonProduct.title}
                    </motion.div>
                  </motion.div>
                </motion.div>
              </motion.div> : <motion.div layout initial={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.5
          }} data-id="element-379">
                <div className="p-4 space-y-4" data-id="element-380">
                  <motion.div layout className="grid grid-cols-2 gap-8 border-b border-gray-100 pb-4" exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.4
              }} data-id="element-381">
                    <div className="flex items-center space-x-2" data-id="element-382">
                      <ShoppingBagIcon className="h-5 w-5 text-[#7AB55C]" data-id="element-383" />
                      <span className="font-medium text-gray-700" data-id="element-384">
                        Shopify Product
                      </span>
                    </div>
                    <div className="flex items-center space-x-2" data-id="element-385">
                      <BoxIcon className="h-5 w-5 text-[#FF9900]" data-id="element-386" />
                      <span className="font-medium text-gray-700" data-id="element-387">
                        Amazon Match
                      </span>
                    </div>
                  </motion.div>
                  <motion.div layout className="flex items-stretch" data-id="element-388">
                    <motion.div layout className="flex-1 pr-8 border-r border-gray-100" data-id="element-389">
                      <motion.div layout className="space-y-4" data-id="element-390">
                        <motion.div layout exit={{
                      opacity: 0,
                      height: 0
                    }} transition={{
                      duration: 0.4
                    }} className="w-24 h-24 rounded-lg overflow-hidden bg-gray-100" data-id="element-391">
                          <img src={product.imageUrl} alt={product.title} className="w-full h-full object-cover" data-id="element-392" />
                        </motion.div>
                        <motion.div layout className="space-y-2" data-id="element-393">
                          <motion.div layout className="flex items-center space-x-2" data-id="element-394">
                            <motion.div exit={{
                          opacity: 0,
                          width: 0
                        }} transition={{
                          duration: 0.4
                        }} data-id="element-395">
                              <PackageIcon className="h-4 w-4 text-gray-400" data-id="element-396" />
                            </motion.div>
                            <span className="text-gray-600" data-id="element-397">
                              {product.title}
                            </span>
                          </motion.div>
                          <motion.div layout exit={{
                        opacity: 0,
                        height: 0
                      }} transition={{
                        duration: 0.4
                      }} className="flex items-center space-x-2" data-id="element-398">
                            <TagIcon className="h-4 w-4 text-gray-400" data-id="element-399" />
                            <span className="text-gray-600" data-id="element-400">
                              SKU: {product.sku}
                            </span>
                          </motion.div>
                          <motion.div layout exit={{
                        opacity: 0,
                        height: 0
                      }} transition={{
                        duration: 0.4
                      }} className="flex items-center space-x-2" data-id="element-401">
                            <DollarSignIcon className="h-4 w-4 text-gray-400" data-id="element-402" />
                            <span className="text-gray-600" data-id="element-403">
                              ${product.price}
                            </span>
                          </motion.div>
                        </motion.div>
                      </motion.div>
                    </motion.div>
                    <motion.div layout className="px-8 flex flex-col items-center justify-center" data-id="element-404">
                      <motion.div layout className="bg-blue-50 rounded-full p-2" data-id="element-405">
                        <ArrowRightIcon className="h-4 w-4 text-blue-500" data-id="element-406" />
                      </motion.div>
                    </motion.div>
                    <motion.div layout className="flex-1 pl-8 border-l border-gray-100" data-id="element-407">
                      <motion.div layout className="space-y-4" data-id="element-408">
                        <motion.div layout exit={{
                      opacity: 0,
                      height: 0
                    }} transition={{
                      duration: 0.4
                    }} className="w-24 h-24 rounded-lg overflow-hidden bg-gray-100" data-id="element-409">
                          <img src={product.amazonProduct.imageUrl} alt={product.amazonProduct.title} className="w-full h-full object-cover" data-id="element-410" />
                        </motion.div>
                        <motion.div layout className="space-y-2" data-id="element-411">
                          <motion.div layout className="flex items-center space-x-2" data-id="element-412">
                            <motion.div exit={{
                          opacity: 0,
                          width: 0
                        }} transition={{
                          duration: 0.4
                        }} data-id="element-413">
                              <PackageIcon className="h-4 w-4 text-gray-400" data-id="element-414" />
                            </motion.div>
                            <span className="text-gray-800" data-id="element-415">
                              {product.amazonProduct.title}
                            </span>
                          </motion.div>
                          <motion.div layout exit={{
                        opacity: 0,
                        height: 0
                      }} transition={{
                        duration: 0.4
                      }} className="flex items-center space-x-2" data-id="element-416">
                            <TagIcon className="h-4 w-4 text-gray-400" data-id="element-417" />
                            <span className="text-gray-800" data-id="element-418">
                              ASIN: {product.amazonProduct.asin}
                            </span>
                          </motion.div>
                          <motion.div layout exit={{
                        opacity: 0,
                        height: 0
                      }} transition={{
                        duration: 0.4
                      }} className="flex items-center space-x-2" data-id="element-419">
                            <DollarSignIcon className="h-4 w-4 text-gray-400" data-id="element-420" />
                            <span className="text-gray-800" data-id="element-421">
                              ${product.amazonProduct.price}
                            </span>
                          </motion.div>
                          <motion.div layout exit={{
                        opacity: 0,
                        height: 0
                      }} transition={{
                        duration: 0.4
                      }} className="flex items-center space-x-3" data-id="element-422">
                            <div className="flex items-center text-yellow-500" data-id="element-423">
                              <StarIcon className="h-4 w-4 fill-current" data-id="element-424" />
                              <span className="ml-1 text-sm" data-id="element-425">
                                {product.amazonProduct.rating}
                              </span>
                            </div>
                            <span className="text-xs text-gray-500" data-id="element-426">
                              {product.amazonProduct.reviewCount.toLocaleString()}{' '}
                              reviews
                            </span>
                          </motion.div>
                        </motion.div>
                      </motion.div>
                    </motion.div>
                  </motion.div>
                </div>
                <motion.div layout exit={{
              opacity: 0
            }} transition={{
              duration: 0.4
            }} className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-end space-x-3" data-id="element-427">
                  <button className="text-sm text-gray-600 hover:text-gray-900 flex items-center" data-id="element-428">
                    <ClockIcon className="h-4 w-4 mr-1" data-id="element-429" />
                    Skip for now
                  </button>
                  <motion.button onClick={handleLink} className="bg-blue-600 text-white px-3 py-1.5 rounded-md text-sm flex items-center" whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} data-id="element-430">
                    Link Products
                    <ArrowRightIcon className="h-3 w-3 ml-2" data-id="element-431" />
                  </motion.button>
                </motion.div>
              </motion.div>}
          </AnimatePresence>
        </motion.div>
      </div>;
  }
  // Special case for Arrow Transform animation variant
  if (animationVariant === 'arrowTransform') {
    return <div className="mb-8" data-id="element-432">
        <h2 className="text-lg font-semibold text-gray-800 mb-3" data-id="element-433">{title}</h2>
        <motion.div layout className="bg-white rounded-lg shadow-sm overflow-hidden border relative border-gray-200" data-id="element-434">
          <div className="p-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between" data-id="element-435">
            <div className="flex items-center space-x-2" data-id="element-436">
              <CheckIcon className="h-4 w-4 text-green-500" data-id="element-437" />
              <h3 className="text-sm font-medium text-gray-700" data-id="element-438">
                Perfect Match
              </h3>
            </div>
          </div>
          <AnimatePresence mode="wait" data-id="element-439">
            {isLinked ? <motion.div layout initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.4
          }} className="p-3 flex items-center justify-between" data-id="element-440">
                <motion.div layout className="flex items-stretch w-full" data-id="element-441">
                  <motion.div layout className="flex-1 pr-4" data-id="element-442">
                    <motion.div className="text-sm font-medium text-gray-700 truncate" data-id="element-443">
                      {product.title}
                    </motion.div>
                  </motion.div>
                  <motion.div layout className="flex items-center justify-center w-12" data-id="element-444">
                    <motion.div className="bg-blue-100 p-2 rounded-full" data-id="element-445">
                      <LinkIcon className="h-4 w-4 text-blue-600" data-id="element-446" />
                    </motion.div>
                  </motion.div>
                  <motion.div layout className="flex-1 pl-4" data-id="element-447">
                    <motion.div className="text-sm font-medium text-gray-800 truncate" data-id="element-448">
                      {product.amazonProduct.title}
                    </motion.div>
                  </motion.div>
                </motion.div>
                <motion.button onClick={handleUndo} className="ml-4 flex items-center text-sm text-green-600 hover:text-green-700 flex-shrink-0" whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-449">
                  <UndoIcon className="h-4 w-4 mr-1" data-id="element-450" />
                  Undo
                </motion.button>
              </motion.div> : isSlimmed ? <motion.div layout initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.4
          }} className="p-3 flex items-center justify-between" data-id="element-451">
                <motion.div layout className="flex items-stretch w-full" data-id="element-452">
                  <motion.div layout className="flex-1 pr-4" data-id="element-453">
                    <motion.div className="text-sm font-medium text-gray-700 truncate" data-id="element-454">
                      {product.title}
                    </motion.div>
                  </motion.div>
                  <motion.div layout className="flex items-center justify-center w-12" style={{
                perspective: 800
              }} data-id="element-455">
                    <AnimatePresence mode="wait" data-id="element-456">
                      {!showArrowTransform ? <motion.div key="arrow" layout className="bg-blue-50 rounded-full p-2" exit={{
                    rotateY: 90,
                    opacity: 0
                  }} transition={{
                    duration: 0.3,
                    type: 'spring',
                    stiffness: 100
                  }} data-id="element-457">
                          <ArrowRightIcon className="h-4 w-4 text-blue-500" data-id="element-458" />
                        </motion.div> : <motion.div key="link" layout className="bg-blue-100 p-2 rounded-full" initial={{
                    rotateY: -90,
                    opacity: 0
                  }} animate={{
                    rotateY: 0,
                    opacity: 1
                  }} transition={{
                    duration: 0.3,
                    type: 'spring',
                    stiffness: 100
                  }} onAnimationComplete={() => {
                    setTimeout(() => {
                      setIsLinked(true);
                    }, 300);
                  }} data-id="element-459">
                          <LinkIcon className="h-4 w-4 text-blue-600" data-id="element-460" />
                        </motion.div>}
                    </AnimatePresence>
                  </motion.div>
                  <motion.div layout className="flex-1 pl-4" data-id="element-461">
                    <motion.div className="text-sm font-medium text-gray-800 truncate" data-id="element-462">
                      {product.amazonProduct.title}
                    </motion.div>
                  </motion.div>
                </motion.div>
              </motion.div> : <motion.div layout initial={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.5
          }} data-id="element-463">
                <div className="p-4 space-y-4" data-id="element-464">
                  <motion.div layout className="grid grid-cols-2 gap-8 border-b border-gray-100 pb-4" exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.4
              }} data-id="element-465">
                    <div className="flex items-center space-x-2" data-id="element-466">
                      <ShoppingBagIcon className="h-5 w-5 text-[#7AB55C]" data-id="element-467" />
                      <span className="font-medium text-gray-700" data-id="element-468">
                        Shopify Product
                      </span>
                    </div>
                    <div className="flex items-center space-x-2" data-id="element-469">
                      <BoxIcon className="h-5 w-5 text-[#FF9900]" data-id="element-470" />
                      <span className="font-medium text-gray-700" data-id="element-471">
                        Amazon Match
                      </span>
                    </div>
                  </motion.div>
                  <motion.div layout className="flex items-stretch" data-id="element-472">
                    <motion.div layout className="flex-1 pr-8 border-r border-gray-100" data-id="element-473">
                      <motion.div layout className="space-y-4" data-id="element-474">
                        <motion.div layout exit={{
                      opacity: 0,
                      height: 0
                    }} transition={{
                      duration: 0.4
                    }} className="w-24 h-24 rounded-lg overflow-hidden bg-gray-100" data-id="element-475">
                          <img src={product.imageUrl} alt={product.title} className="w-full h-full object-cover" data-id="element-476" />
                        </motion.div>
                        <motion.div layout className="space-y-2" data-id="element-477">
                          <motion.div layout className="flex items-center space-x-2" data-id="element-478">
                            <motion.div exit={{
                          opacity: 0,
                          width: 0
                        }} transition={{
                          duration: 0.4
                        }} data-id="element-479">
                              <PackageIcon className="h-4 w-4 text-gray-400" data-id="element-480" />
                            </motion.div>
                            <span className="text-gray-600" data-id="element-481">
                              {product.title}
                            </span>
                          </motion.div>
                          <motion.div layout exit={{
                        opacity: 0,
                        height: 0
                      }} transition={{
                        duration: 0.4
                      }} className="flex items-center space-x-2" data-id="element-482">
                            <TagIcon className="h-4 w-4 text-gray-400" data-id="element-483" />
                            <span className="text-gray-600" data-id="element-484">
                              SKU: {product.sku}
                            </span>
                          </motion.div>
                          <motion.div layout exit={{
                        opacity: 0,
                        height: 0
                      }} transition={{
                        duration: 0.4
                      }} className="flex items-center space-x-2" data-id="element-485">
                            <DollarSignIcon className="h-4 w-4 text-gray-400" data-id="element-486" />
                            <span className="text-gray-600" data-id="element-487">
                              ${product.price}
                            </span>
                          </motion.div>
                        </motion.div>
                      </motion.div>
                    </motion.div>
                    <motion.div layout className="px-8 flex flex-col items-center justify-center" data-id="element-488">
                      <motion.div layout className="bg-blue-50 rounded-full p-2" data-id="element-489">
                        <ArrowRightIcon className="h-4 w-4 text-blue-500" data-id="element-490" />
                      </motion.div>
                    </motion.div>
                    <motion.div layout className="flex-1 pl-8 border-l border-gray-100" data-id="element-491">
                      <motion.div layout className="space-y-4" data-id="element-492">
                        <motion.div layout exit={{
                      opacity: 0,
                      height: 0
                    }} transition={{
                      duration: 0.4
                    }} className="w-24 h-24 rounded-lg overflow-hidden bg-gray-100" data-id="element-493">
                          <img src={product.amazonProduct.imageUrl} alt={product.amazonProduct.title} className="w-full h-full object-cover" data-id="element-494" />
                        </motion.div>
                        <motion.div layout className="space-y-2" data-id="element-495">
                          <motion.div layout className="flex items-center space-x-2" data-id="element-496">
                            <motion.div exit={{
                          opacity: 0,
                          width: 0
                        }} transition={{
                          duration: 0.4
                        }} data-id="element-497">
                              <PackageIcon className="h-4 w-4 text-gray-400" data-id="element-498" />
                            </motion.div>
                            <span className="text-gray-800" data-id="element-499">
                              {product.amazonProduct.title}
                            </span>
                          </motion.div>
                          <motion.div layout exit={{
                        opacity: 0,
                        height: 0
                      }} transition={{
                        duration: 0.4
                      }} className="flex items-center space-x-2" data-id="element-500">
                            <TagIcon className="h-4 w-4 text-gray-400" data-id="element-501" />
                            <span className="text-gray-800" data-id="element-502">
                              ASIN: {product.amazonProduct.asin}
                            </span>
                          </motion.div>
                          <motion.div layout exit={{
                        opacity: 0,
                        height: 0
                      }} transition={{
                        duration: 0.4
                      }} className="flex items-center space-x-2" data-id="element-503">
                            <DollarSignIcon className="h-4 w-4 text-gray-400" data-id="element-504" />
                            <span className="text-gray-800" data-id="element-505">
                              ${product.amazonProduct.price}
                            </span>
                          </motion.div>
                          <motion.div layout exit={{
                        opacity: 0,
                        height: 0
                      }} transition={{
                        duration: 0.4
                      }} className="flex items-center space-x-3" data-id="element-506">
                            <div className="flex items-center text-yellow-500" data-id="element-507">
                              <StarIcon className="h-4 w-4 fill-current" data-id="element-508" />
                              <span className="ml-1 text-sm" data-id="element-509">
                                {product.amazonProduct.rating}
                              </span>
                            </div>
                            <span className="text-xs text-gray-500" data-id="element-510">
                              {product.amazonProduct.reviewCount.toLocaleString()}{' '}
                              reviews
                            </span>
                          </motion.div>
                        </motion.div>
                      </motion.div>
                    </motion.div>
                  </motion.div>
                </div>
                <motion.div layout exit={{
              opacity: 0
            }} transition={{
              duration: 0.4
            }} className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-end space-x-3" data-id="element-511">
                  <button className="text-sm text-gray-600 hover:text-gray-900 flex items-center" data-id="element-512">
                    <ClockIcon className="h-4 w-4 mr-1" data-id="element-513" />
                    Skip for now
                  </button>
                  <motion.button onClick={handleLink} className="bg-blue-600 text-white px-3 py-1.5 rounded-md text-sm flex items-center" whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} data-id="element-514">
                    Link Products
                    <ArrowRightIcon className="h-3 w-3 ml-2" data-id="element-515" />
                  </motion.button>
                </motion.div>
              </motion.div>}
          </AnimatePresence>
        </motion.div>
      </div>;
  }
  return <div className="mb-8" data-id="element-516">
      <h2 className="text-lg font-semibold text-gray-800 mb-3" data-id="element-517">{title}</h2>
      <motion.div layout className="bg-white rounded-lg shadow-sm overflow-hidden border relative border-gray-200" data-id="element-518">
        <AnimatePresence mode="wait" data-id="element-519">
          {isLinked ? <motion.div key="linked" initial={selectedVariant.initial} animate={selectedVariant.animate} exit={selectedVariant.exit} transition={selectedVariant.transition} className="p-3 flex items-center justify-between bg-green-50" data-id="element-520">
              <div className="flex items-center min-w-0" data-id="element-521">
                <CheckIcon className="h-4 w-4 flex-shrink-0 mr-2 text-green-500" data-id="element-522" />
                <div className="min-w-0" data-id="element-523">
                  <span className="text-sm font-medium truncate block text-green-700" data-id="element-524">
                    {product.title}
                  </span>
                  <span className="text-xs text-green-600" data-id="element-525">
                    Product Added for Linking
                  </span>
                </div>
              </div>
              <motion.button onClick={handleUndo} className="ml-4 flex items-center text-sm text-green-600 hover:text-green-700" whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} data-id="element-526">
                <UndoIcon className="h-4 w-4 mr-1" data-id="element-527" />
                Undo
              </motion.button>
            </motion.div> : <motion.div key="details" initial={selectedVariant.initial} animate={selectedVariant.animate} exit={selectedVariant.exit} transition={selectedVariant.transition} data-id="element-528">
              <div className="p-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between" data-id="element-529">
                <div className="flex items-center space-x-2" data-id="element-530">
                  <CheckIcon className="h-4 w-4 text-green-500" data-id="element-531" />
                  <h3 className="text-sm font-medium text-gray-700" data-id="element-532">
                    Perfect Match
                  </h3>
                </div>
              </div>
              <div className="p-4 space-y-4" data-id="element-533">
                <div className="grid grid-cols-2 gap-8 border-b border-gray-100 pb-4" data-id="element-534">
                  <div className="flex items-center space-x-2" data-id="element-535">
                    <ShoppingBagIcon className="h-5 w-5 text-[#7AB55C]" data-id="element-536" />
                    <span className="font-medium text-gray-700" data-id="element-537">
                      Shopify Product
                    </span>
                  </div>
                  <div className="flex items-center space-x-2" data-id="element-538">
                    <BoxIcon className="h-5 w-5 text-[#FF9900]" data-id="element-539" />
                    <span className="font-medium text-gray-700" data-id="element-540">
                      Amazon Match
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-4" data-id="element-541">
                  <div className="w-24 h-24 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0" data-id="element-542">
                    <img src={product.imageUrl} alt={product.title} className="w-full h-full object-cover" data-id="element-543" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-544">
                    <ComparisonRow label="Title" shopify={product.title} amazon={product.amazonProduct.title} icon={<PackageIcon className="h-4 w-4 text-gray-400" data-id="element-546" />} data-id="element-545" />
                    <ComparisonRow label="ID" shopify={`SKU: ${product.sku}`} amazon={`ASIN: ${product.amazonProduct.asin}`} icon={<TagIcon className="h-4 w-4 text-gray-400" data-id="element-548" />} data-id="element-547" />
                    <ComparisonRow label="Price" shopify={`$${product.price}`} amazon={`$${product.amazonProduct.price}`} icon={<DollarSignIcon className="h-4 w-4 text-gray-400" data-id="element-550" />} data-id="element-549" />
                  </div>
                  <div className="flex-shrink-0 flex flex-col items-end space-y-1" data-id="element-551">
                    <div className="flex items-center text-yellow-500" data-id="element-552">
                      <StarIcon className="h-4 w-4 fill-current" data-id="element-553" />
                      <span className="ml-1 text-sm" data-id="element-554">
                        {product.amazonProduct.rating}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500" data-id="element-555">
                      {product.amazonProduct.reviewCount.toLocaleString()}{' '}
                      reviews
                    </span>
                  </div>
                </div>
              </div>
              <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-end space-x-3" data-id="element-556">
                <button className="text-sm text-gray-600 hover:text-gray-900 flex items-center" data-id="element-557">
                  <ClockIcon className="h-4 w-4 mr-1" data-id="element-558" />
                  Skip for now
                </button>
                <motion.button onClick={handleLink} className="bg-blue-600 text-white px-3 py-1.5 rounded-md text-sm flex items-center" whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-559">
                  Link Products
                  <ArrowRightIcon className="h-3 w-3 ml-2" data-id="element-560" />
                </motion.button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </motion.div>
    </div>;
};
const AnimationVariationsPage = () => {
  return <div className="min-h-screen bg-gray-50" data-id="element-561">
      <Header data-id="element-562" />
      <div className="max-w-7xl mx-auto px-4 py-6" data-id="element-563">
        <div className="flex items-center mb-6" data-id="element-564">
          <Link to="/" className="mr-4 p-2 hover:bg-gray-100 rounded-full" data-id="element-565">
            <ArrowRightIcon className="h-5 w-5 text-gray-600 rotate-180" data-id="element-566" />
          </Link>
          <h1 className="text-xl font-bold text-gray-800" data-id="element-567">
            Animation Variations
          </h1>
        </div>
        <div className="bg-white rounded-lg shadow p-6 mb-8" data-id="element-568">
          <div className="flex items-start" data-id="element-569">
            <LinkIcon className="h-5 w-5 text-blue-500 mt-0.5" data-id="element-570" />
            <div className="ml-3" data-id="element-571">
              <h3 className="text-sm font-medium text-gray-900" data-id="element-572">
                Animation Test Page
              </h3>
              <p className="text-sm text-gray-500" data-id="element-573">
                This page demonstrates different animation styles for the
                product linking flow. Click "Link Products" on each card to see
                the animation in action.
              </p>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-8" data-id="element-574">
          <AnimationCard title="1. Fade & Slide (Current Animation)" animationVariant="fadeSlide" data-id="element-575" />
          <AnimationCard title="2. Scale & Fade" animationVariant="scaleFade" data-id="element-576" />
          <AnimationCard title="3. Flip Animation" animationVariant="flip" data-id="element-577" />
          <AnimationCard title="4. Bounce Effect" animationVariant="bounce" data-id="element-578" />
          <AnimationCard title="5. Swipe Left/Right" animationVariant="swipe" data-id="element-579" />
          <AnimationCard title="6. Arrow to Link Transform" animationVariant="arrowTransform" data-id="element-580" />
          <AnimationCard title="7. Arrow Transform + Swipe Success" animationVariant="arrowSwipe" data-id="element-581" />
        </div>
      </div>
    </div>;
};
export default AnimationVariationsPage;